<template lang="pug">
    .play-box(:class="{'play-box--warning': isVerticalScreen}")
        .warning(v-if="isVerticalScreen")
            template
                svg#rotate(
                    version='1.1',
                    xmlns='http://www.w3.org/2000/svg',
                    x='0px', y='0px', width='40px', height='40px',
                    viewBox='0 0 612 612',
                    style='enable-background:new 0 0 612 612;',
                    xml:space='preserve'
                )
                    rotation-icon
                div
                    | {{ $i18n.t('map.map_mobile_rotateText') }}
        template(v-else)
            template(v-if="!isLoading")
                game-header(
                    @soundStateChanged="headerKey = !headerKey",
                    :key="headerKey",
                    :time="getTime",
                    :slug="type",
                    :game-type="parameters && parameters.courseType",
                    :is-homework="!!exerciseId",
                )
                .play-box__game(
                    v-if="!gameFinished  && gameStarted"
                    :class="'play-box__game--tables'"
                )
                    keen-eye-table(
                        v-if="colors.length"
                        :colors="colors"
                        :is-shown="isTableShown"
                        :display-speed="displaySpeed"
                        @handleClick ='handleClick'
                        :key="colors && `${colors.length} x ${colors[0].length}`"
                    )
                .game-info__container(v-if="!gameFinished")
                    .game-info__block
                        game-info(
                            :score="{left: answers.good, right: answers.bad}"
                            :time="getTime"
                            :slug="type"
                            :parameters="parameters"
                            :game-allows-tips="true"
                        )
                    .keen-eye__buttons-container(v-show='isTableShown && displaySpeed === 0')
                        cs-button.keen-eye__button(
                            color="blue"
                            @click='isTableShown = !isTableShown'
                            v-focus=''
                        )
                            | {{ memorizedButtonName }}
                    .game-task__block(v-if='!isTableShown')
                        | {{$i18n.t('common.common_gameName_search')}}
                        .game-task__block-color(:style="{backgroundColor: currentColorToFind}")
                .play-box__end-container(v-if="gameFinished")
                    fireworks(
                        v-if="gameFinished"
                        :is-mobile="isMobile"
                    )
                    .play-box__reaction-container
                        .play-box__time
                            | {{ timeExecutionString }}
                        .play-box__reaction
                            .play-box__reaction-side.play-box__reaction-side--correct
                                div
                                    | {{ $i18n.t('trainer.trainer_gamePlayBox_correct') }}
                                .play-box__reaction-count
                                    | {{ answers.good }}
                            result-image.play-box__image(:status="passedSuccessfully ? 'success' : 'failure'")
                            .play-box__reaction-side.play-box__reaction-side--incorrect
                                div
                                    | {{ $i18n.t('trainer.trainer_gamePlayBox_wrong') }}
                                .play-box__reaction-count
                                    | {{ answers.bad }}

                    result-buttons(
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        show-restart-button
                        @restartGame="restartGame"
                    )

</template>

<script>
    import KeenEyeTable from './KeenEyeTable.vue';
    import { shuffle, groupBy, chunk, cloneDeep } from 'lodash';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import EndGameMixin from '../../../../utils/trainer/endGame';
    import GameHeader from '../../GameHeader.vue';
    import GameInfo from '../GameInfo.vue';
    import Fireworks from '../Fireworks.vue';
    import RotationIcon from '../../../../components/svg/RotationIcon.vue';
    import ResultImage from '../ResultImage.vue';
    import CalculatingAnswers from '../../CalculatingAnswersModal.vue';
    import KeenEyeConfig from '../../configs/KeenEyeConfig';
    import timer from '../../../../utils/trainer/timer';
    import MobileDetect from 'mobile-detect';

    const DEFAULT_AMOUNT_OF_COLORED_CELLS = 2;

    function DEFAULT_DATA() {
        return {
            difficult: null,
            timeLimit: 0,
            displaySpeed: 0,
            columns: null,
            rows: null,
            amountOfColoredCells: DEFAULT_AMOUNT_OF_COLORED_CELLS,
            selectedColors: null,
            colors: [],
            correctAnswers: [],
            correctAnswersInOneSubLevel: 0,
            currentColorToFind: null,
            isTableShown: false,
            health: 3,
            subLevelsAmount: 0,
            subLevel: 1,
            level: null,
            startAmountOfLevels: KeenEyeConfig.difficulties.length,
            amountOfLevels: 0,
            filledCells: [],
            godMode: false,
            passedSuccessfully: null,
            timeLimitSeconds: 0,
            answers: {
                good: 0,
                bad: 0,
            },
            correctClickedCells: [],
            savedTime: 0,
            isTableShownTimer: null,
        };
    }
    export default {
        mixins: [playBoxGameMixin, EndGameMixin, timer],
        created() {
            window.addEventListener('beforeunload', this.beforeTabClosed);
        },
        mounted() {
            this.isTablet = Boolean(new MobileDetect(window.navigator.userAgent).tablet());
        },
        beforeDestroy() {
            this.beforeTabClosed();
            clearTimeout(this.timeLimitTimer);
            window.removeEventListener('beforeunload', this.beforeTabClosed);
        },
        watch: {
            correctAnswers: {
                handler(value) {
                    if (value.length > 0) {
                        this.currentColorToFind = value[0].color;
                    } else {
                        this.checkForSwitchLevel();
                    }
                },
            },
            health(value) {
                if (value === 0) {
                    this.passedSuccessfully = false;
                    clearTimeout(this.timer);
                    this.finishGame();
                }
            },
            amountOfLevels(value) {
                if (value === 0) {
                    this.passedSuccessfully = true;
                    clearTimeout(this.timer);
                    this.finishGame();
                }
            },
            subLevel(newValue) {
                if (newValue) {
                    this.health = 3;
                }
                if (newValue > this.subLevelsAmount || (this.level === 1 && newValue >= this.subLevelsAmount)) {
                    this.subLevel = 1;
                    this.level++;
                }
            },
            level(newValue) {
                if (newValue) {
                    this.filledCells.forEach(cell => (cell.style.backgroundColor = ''));
                    this.correctAnswers.length = 0;
                    this.isTableShown = true;
                    this.columns = KeenEyeConfig.difficulties[this.level - 1].columns;
                    this.rows = KeenEyeConfig.difficulties[this.level - 1].rows;
                    this.colors = this.generateTable();
                    this.getCorrectAnswers();
                    if (!this.isVerticalScreen) this.setDisplaySpeed();
                }
            },
            timeLimitSeconds(val) {
                if (val >= this.timeLimit * 60 && this.timeLimit !== 0 && !this.gameFinished) {
                    clearTimeout(this.timeLimitTimer);
                    clearTimeout(this.timer);
                    this.passedSuccessfully = this.answers.good > this.answers.bad;
                    this.finishGame();
                }
            },
            timeLimit(val) {
                if (val > 0) {
                    this.setTimeLimitTimer();
                }
            },
            isVerticalScreen(val) {
                if (val) {
                    this.savedTime = this.time;
                } else {
                    !this.timer && this.setTimer();
                    this.time = this.savedTime;
                    if (!this.isTableShownTimer) this.setDisplaySpeed();
                }
            },
        },
        data() {
            return DEFAULT_DATA();
        },
        methods: {
            restartGame() {
                this.beforeGameRestart && this.beforeGameRestart();
                Object.assign(this, DEFAULT_DATA());
                this.assignDataFromParameters();
                clearTimeout(this.counterTimeout);
                this.resetTimer();
                this.fetchData();
            },
            fetchData() {
                this.getExercise()
                    .then(() => {
                        this.assignDataFromParameters();

                        this.$createjs.Sound.removeAllSounds();
                        this.$createjs.Sound.removeAllEventListeners();
                        this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                        this.$createjs.Sound.registerSounds(this.soundObjects);

                        this.beforeStart();
                        this.isLoading = false;
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            beforeStart() {
                this.gameFinished = false;
                this.gameStarted = true;
                !this.isVerticalScreen && !this.timer && this.setTimer();
            },
            assignDataFromParameters() {
                this.timeLimit = this.parameters.timeLimit;
                this.displaySpeed = this.parameters.displaySpeed;
                this.selectedColors = this.parameters.selectedColors;
                this.level = this.parameters.difficult.level;
                this.columns = this.parameters.difficult.columns;
                this.rows = this.parameters.difficult.rows;
                this.godMode = this.timeLimit > 0;
            },
            generateTable() {
                this.subLevelsAmount = this.rows;
                this.amountOfLevels = this.startAmountOfLevels - this.level;
                this.amountOfColoredCells = this.level;
                if (this.level === 1) {
                    this.amountOfColoredCells = 2;
                }
                if (this.level > 1 && this.level < 5) {
                    this.amountOfColoredCells = this.level + this.subLevel;
                }
                if (this.level > 4 && this.subLevel !== 1) {
                    this.amountOfColoredCells = this.level + this.subLevel - 1;
                }
                let availableColorsArray = [];
                this.getRandomColorArray(this.selectedColors).forEach(color => {
                    let amountOfSpecialColor = Math.round(this.amountOfColoredCells * color.percent);
                    for (let i = 0; i < amountOfSpecialColor; i++) {
                        if (availableColorsArray.length >= this.amountOfColoredCells) {
                            break;
                        }
                        availableColorsArray.push(color.value);
                    }
                });
                const restLength = this.rows * this.columns - availableColorsArray.length;
                let arrayOfColors = [...availableColorsArray, ...Array(restLength).fill(null)];
                arrayOfColors = shuffle(arrayOfColors);
                return chunk(arrayOfColors, this.columns);
            },
            getRandomColorArray(array) {
                let currentIndex = array.length;
                this.setPercentForEachColor();
                while (currentIndex !== 0) {
                    let randomIndex = Math.floor(Math.random() * currentIndex);
                    currentIndex--;
                    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
                }
                array.sort((a, b) => a.percent - b.percent);
                return array;
            },
            setPercentForEachColor() {
                switch (this.selectedColors.length) {
                    case 1:
                        this.selectedColors[0].percent = 1;
                        break;
                    case 2:
                        this.selectedColors[0].percent = 0.4;
                        this.selectedColors[1].percent = 0.6;
                        break;
                    case 3:
                        this.selectedColors[0].percent = 0.2;
                        this.selectedColors[1].percent = 0.3;
                        this.selectedColors[2].percent = 0.5;
                        break;
                    default:
                        throw new Error('Invalid selectedColors length');
                }
            },
            handleClick({ selectedCell, color, rowIndex, columnIndex }) {
                if (this.currentColorToFind === color) {
                    if (this.correctClickedCells.find(cell => cell.row === rowIndex && cell.column === columnIndex)) {
                        return;
                    }
                    this.correctClickedCells.push({ row: rowIndex, column: columnIndex });
                    this.$createjs.Sound.play('clickCorrect');
                    this.filledCells.push(selectedCell);
                    selectedCell.style.backgroundColor = color;
                    this.correctAnswers.shift();
                    this.answers.good++;
                    if (!(this.answers.good % this.amountOfColoredCells)) {
                        this.correctAnswersInOneSubLevel++;
                    }
                    if (this.correctAnswers.length === 0) {
                        this.correctClickedCells = [];
                        this.checkForSwitchLevel();
                    }
                } else {
                    selectedCell.classList.add('shake', 'keen-eye-table__press-item_white');
                    this.$createjs.Sound.play('clickIncorrect');
                    setTimeout(() => {
                        selectedCell.classList.remove('shake', 'keen-eye-table__press-item_white');
                    }, 800);
                    this.answers.bad++;
                    if (!this.godMode) {
                        this.health--;
                    }
                }
            },
            getCorrectAnswers() {
                for (let row = 0; row < this.colors.length; row++) {
                    for (let column = 0; column < this.colors[row].length; column++) {
                        if (this.colors[row][column] !== null) {
                            this.correctAnswers.push({
                                row: row,
                                column: column,
                                color: this.colors[row][column],
                            });
                        }
                    }
                }
                const groupedAnswersByColor = groupBy(this.correctAnswers, answer => answer.color);
                this.correctAnswers = shuffle(Object.values(groupedAnswersByColor)).flat();
            },
            checkForSwitchLevel() {
                if (
                    (this.correctAnswersInOneSubLevel === 2 && this.level === 1) ||
                    this.correctAnswersInOneSubLevel === 3
                ) {
                    this.correctAnswersInOneSubLevel = 0;
                    this.subLevel++;
                    return;
                }
                this.filledCells.forEach(cell => (cell.style.backgroundColor = ''));
                this.isTableShown = true;
                this.colors = this.generateTable();
                this.getCorrectAnswers();
                this.setDisplaySpeed();
            },
            setDisplaySpeed() {
                if (this.displaySpeed > 0) {
                    this.isTableShownTimer = setTimeout(() => {
                        this.isTableShown = false;
                    }, this.displaySpeed * 1000);
                } else {
                    this.isTableShown = true;
                }
            },
            refreshGameStatBeforeSend() {
                this.gameStat.attempts = this.answers.good + this.answers.bad;
                this.gameStat.answers = this.answers.good;
                this.gameStat.chainOfAttempts = [];
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = this.gameFinished;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            calculatePercent() {
                let total = this.answers.good + this.answers.bad;
                if (total > 0) {
                    return parseInt((this.answers.good / total) * 100);
                }
                return 0;
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            beforeTabClosed() {
                if (this.gameStarted && this.user.type === 'student') {
                    this.finishGame();
                }
                return null;
            },
            setTimeLimitTimer() {
                this.timeLimitSeconds++;
                this.timeLimitTimer = setTimeout(this.setTimeLimitTimer, 1000);
            },
            finishGame() {
                this.gameFinished = true;
                this.sendStat();
            },
        },
        computed: {
            timeExecutionString() {
                return (
                    this.$i18n.t('progress.progress_table_executionTime') +
                    ': ' +
                    this.time +
                    ' ' +
                    this.$i18n.tc('olympiads.olympiads_timeDeclination_seconds', this.time)
                );
            },
            memorizedButtonName() {
                return this.$i18n.t('trainer.trainer_gamePlayBox_memorizedButton', {
                    mode: this.$i18n.t('trainer.trainer_gamePlayBox_memorizedButton_all'),
                });
            },
        },
        components: {
            Fireworks,
            GameInfo,
            GameHeader,
            KeenEyeTable,
            RotationIcon,
            CalculatingAnswers,
            ResultImage,
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box {
        height: 100%;
    }

    .play-box--warning {
        display: table;
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .play-box__game {
        display: flex;
        justify-content: center;
        align-items: stretch;
        flex: 1 1 auto;
    }

    .play-box__game--tables {
        justify-content: space-between;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
    }

    .play-box__time {
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
        text-align: center;
        color: $hsl-color-green-58;
        margin-bottom: 30px;
    }

    .play-box__image {
        max-width: 100%;
        display: none;
        @include media($sm) {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            align-items: center;
            min-height: 300px;
        }
        @include media($md) {
            margin-bottom: 40px;
            min-height: 450px;
        }
        @include media($xlg) {
            min-height: 600px;
        }
    }
    .keen-eye__buttons-container {
        display: flex;
        justify-content: center;
        text-align: center;
        margin: 40px 0;
    }
    .keen-eye__button {
        width: 150px;
        @include media($lg) {
            width: 200px;
        }
    }
    .game-info__container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    .game-task__block {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 150px;
        height: 140px;
        border: 2px solid #1f87ff;
        background-color: #fff;
        border-radius: 10%;
        @include media($lg) {
            height: 230px;
            width: 100%;
        }
    }
    .game-task__block-color {
        margin-top: 25px;
        width: 55%;
        height: 55%;
        border-radius: 10%;
    }
    .play-box__reaction {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        flex-grow: 1;
        flex-shrink: 1;
        width: 100%;
        @include stack-space($space-base);
        flex-direction: row;
        @include media($md) {
            flex-wrap: nowrap;
        }
        @media screen and (max-width: 655px) {
            justify-content: space-between;
        }
    }

    .play-box__reaction-side {
        align-self: normal;
        font-weight: 500;
        font-size: 20px;
        @include media($sm) {
            width: auto;
            font-size: 20px;
        }
        @include media($md) {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            flex-grow: 1;
            align-self: center;
            font-size: 28px;
        }
        @media screen and ($md) and (max-width: 800px) {
            font-size: 18px;
        }
        @include media($lg) {
            font-size: 32px;
        }
        @include media($xlg) {
            font-size: 42px;
        }
        color: $hsl-color-green-58;
        @include stack-space($space-base);
        @include media($sm) {
            @include stack-space(0);
        }
        @media screen and (max-width: 768px) {
            width: 50%;
        }
    }

    .play-box__reaction-side--correct {
        color: $hsl-color-green-58;
    }

    .play-box__reaction-side--incorrect {
        color: $hsl-color-red-62;
    }
</style>
